import type { FC } from 'react';
import { useParams } from 'react-router-dom';
import type {
  ContractManagementResponse,
  ShipperResponse,
} from 'src/apis/contract-management/types';
import type { ShipperPlatformIdsRequest } from 'src/apis/monolith/use-shipper-platform-ids';
import { useOrganisationDetails } from 'src/apis/organisation-service/use-organisation-details';
import type { OrganisationServiceRequest } from 'src/apis/organisation-service/use-organisation-service';
import { Sidebar } from 'src/components/layout/sidebar';
import { ExternalLink, Link } from 'src/components/navigation/link';
import { PageSpinner } from 'src/components/spinner-container';
import type { Successful } from 'src/hooks/use-axios';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { userAssignmentStatusValues } from 'src/pages/shippers/organisations-and-users/schema';
import { useDocumentExchangeRequest } from 'src/pages/shippers/upload-documents/use-document-exchange';

type ShipperInformationSidebarProps = {
  contractManagement?: ContractManagementResponse;
  contractManagementShipper?: ShipperResponse | '';
  shipperPlatformIds?: Successful<ShipperPlatformIdsRequest>;
  organisationService: Successful<OrganisationServiceRequest>;
};

export const ShipperInformationSidebar: FC<ShipperInformationSidebarProps> = ({
  contractManagement,
  contractManagementShipper,
  shipperPlatformIds,
  organisationService,
}) => {
  const organisationDetails = useOrganisationDetails({
    url: organisationService.response.data._links.getMyOrganisationDetails.href,
  });
  const shipperRefId = shipperPlatformIds?.response.data.referenceId.toString();
  const { shipperUuid } = useParams<{ shipperUuid: string }>();
  const monolithUser = useOptionalAuthenticatedMonolithUser();

  const documentExchange = useDocumentExchangeRequest();

  if (!documentExchange.response || !organisationDetails.response)
    return <PageSpinner />;

  const showDocumentExchange =
    documentExchange.response.status !== 404 &&
    documentExchange.response.data._links.getOperatorDocumentsOutbox?.href;

  return (
    <Sidebar>
      <Link
        to={{
          pathname: `/dashboard/shippers/${shipperUuid}/information`,
          search: userAssignmentStatusValues
            .filter((status) => status !== 'NOT_REQUESTED')
            .map(
              (status, index) =>
                `assignedShipperUsersuserAssignmentStatus[${index}]=${status}`
            )
            .join('&'),
        }}
        mode="sidebar"
      >
        Shipper Overview
      </Link>

      {monolithUser?.isOperator && (
        <ExternalLink
          href={`${PRISMA_CONFIG.angularUrl}/#/shippers/details/${shipperRefId}/credit-limits`}
          mode="sidebar"
        >
          Credit Limits
        </ExternalLink>
      )}

      {organisationService.response.data._links.getAssignedShipperDetails && (
        <Link
          to={`/dashboard/shippers/${shipperUuid}/contact-details`}
          mode="sidebar"
        >
          Contact Details
        </Link>
      )}

      {/* adding a check for document exchange here as there might be a document exchange link for operator,
      which is not known to the monolith. If the operator is known to the monolith, then we show section anyway */}
      {(showDocumentExchange || monolithUser?.isOperator) && (
        <Link
          to={`/dashboard/shippers/${shipperUuid}/documents`}
          mode="sidebar"
        >
          Documents
        </Link>
      )}

      {contractManagement?._links.contracts && (
        <Link
          to={`/dashboard/shippers/${shipperUuid}/contracts`}
          mode="sidebar"
        >
          Contracts
        </Link>
      )}

      {contractManagementShipper &&
        contractManagementShipper._links.getFinancialSecurities && (
          <Link
            to={`/dashboard/shippers/${shipperUuid}/financial-securities`}
            mode="sidebar"
          >
            Financial Securities
          </Link>
        )}
    </Sidebar>
  );
};
